<template>
  <div class="p-4 flex flex-col gap-1.5 h-67.5 glass-frosted w-full rounded-8xl">
    <p class="text-s-medium text-text-glass-default">{{ $t('pageAndPlayer.player.displayTip') }}</p>
    <div class="flex gap-3">
      <div
        v-for="screen in pages"
        :key="screen.slot"
        class="relative w-38.5 h-24 rounded-lg overflow-hidden"
      >
        <img
          v-if="screen.thumbnail && !screen.error"
          :src="screen.thumbnail"
          class="w-38.5 h-24 object-cover"
        />
        <div
          v-else-if="screen.error"
          class="w-full h-full p-2 flex justify-center items-center gap-1 bg-white text-s-medium text-brand-blue1"
        >
          <Icon icon="Multimedia-Fill-Cut" class="size-4" />
          <span>{{ $t('pageAndPlayer.player.imgMissing') }}</span>
        </div>
        <div
          class="absolute inset-0 flex flex-col items-center justify-between p-2 bg-mask20"
          :class="screen.kind === 'custom' ? 'cursor-pointer' : ''"
          @click="screen.kind === 'custom' && emit('select', screen.slot)"
        >
          <div class="flex items-center justify-between w-full">
            <div class="p-1.5 inline-block glass-frosted text-s-medium text-white rounded-lg">
              <span v-if="screen.kind === 'home'">
                {{ $t('pageAndPlayer.pages.systemPage.home') }}
              </span>
              <span v-else-if="screen.kind === 'mouse-rt'">
                {{ $t('pageAndPlayer.pages.systemPage.mouseRt') }}
              </span>
              <span v-else-if="screen.kind === 'custom'">
                {{ screen.thumbname || screen.id }}{{ screen.thumbext }}
              </span>
            </div>
            <Icon
              icon="Switch-Fill-Check"
              class="size-6 text-white transition-opacity"
              :class="screen.kind !== 'custom' || screen.show ? 'opacity-100' : 'opacity-0'"
            />
          </div>
          <div
            class="p-1.5 inline-flex items-center gap-1 h-8 bg-brand-blue1 rounded-full"
            @click="(e) => handleFocusDelay(screen.slot, e)"
          >
            <Icon icon="Writing-Wireframe-Edit" class="size-4 text-white-black" />
            <div class="pr-1">
              <input
                ref="inputRef"
                type="number"
                :value="editingSlot === screen.slot ? draftValue : screen.delay"
                :min="MIN"
                :max="MAX"
                class="no-spinner text-white-black text-s-medium cursor-pointer field-sizing-content"
                @focus="(e) => handleStartEdit(screen.slot, e)"
                @input="handleDraftInput"
                @blur="(e) => handleUpdateDelay(screen.slot, e)"
                @keydown="(e) => ['-', '+'].includes(e.key) && e.preventDefault()"
                @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
              />
              <span class="text-white-black text-s-medium">s</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { shallowRef, useTemplateRef } from 'vue'
  import { Icon } from '@rapoo/element'
  import type { PageItem } from '../../utils/types'

  const MAX = 65535
  const MIN = 3

  const props = defineProps<{
    pages: PageItem[]
  }>()

  const emit = defineEmits<{
    (e: 'select', slot: number): void
    (e: 'update:delay', slot: number, delay: number): void
  }>()

  const inputRefs = useTemplateRef('inputRef')
  const editingSlot = shallowRef<number | null>(null)
  const draftValue = shallowRef('')

  const getInputIndex = (slot: number) => props.pages.findIndex((page) => page.slot === slot)

  const handleFocusDelay = (slot: number, e: PointerEvent) => {
    e.stopPropagation()

    const index = getInputIndex(slot)
    inputRefs.value?.[index]?.focus()
  }

  const clampDelay = (raw: string): number | null => {
    if (raw === '') return null

    const num = Number.parseInt(raw, 10)
    if (Number.isNaN(num)) return MIN

    return Math.min(MAX, Math.max(MIN, num))
  }

  const handleStartEdit = (slot: number, e: FocusEvent) => {
    editingSlot.value = slot
    draftValue.value = (e.target as HTMLInputElement).value
  }

  const handleDraftInput = (e: Event) => {
    draftValue.value = (e.target as HTMLInputElement).value
  }

  const handleUpdateDelay = (slot: number, e: FocusEvent) => {
    const input = e.target as HTMLInputElement
    const delay = clampDelay(input.value) ?? MIN

    if (input.value !== String(delay)) {
      input.value = String(delay)
    }

    if (props.pages.find((page) => page.slot === slot)?.delay !== delay) {
      emit('update:delay', slot, delay)
    }

    editingSlot.value = null
    draftValue.value = ''
  }
</script>

<style scoped>
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .no-spinner {
    appearance: textfield;
    -moz-appearance: textfield;
  }
</style>
